import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import ProductData from 'app/utils/productItem/productData';
import { isIOS } from 'app/utils/browserHelpers';
import mediaQueries from 'app/utils/mediaQueries';
import { saveProductReferrer } from 'app/utils/referrerTracker/referrers';
import { dlTrackProductClick, dlSelectPLPOrSRPItem } from 'app/analytics/productListAnalytics';
import { isLeftArrowPressed, isRightArrowPressed } from 'app/utils/accessibility/accessibilityUtils';
import TrapFocus from 'app/utils/accessibility/trapFocus';
import LoadingIndicator from 'app/products/components/productList/loadingIndicator';
import { HeroBadge, PromoBadge } from 'app/commonComponents/productBadge/heroPromoBadge';
import { injectIntl } from 'react-intl';
import ArrowsAndBottomIcons from './arrowsAndBottomIcons';
import StarRating from '../../starRating/starRating';

const ProductInfoSwitcher = React.lazy(() =>
  import(
    /* webpackChunkName: "ProductInfoSwitcher" */
    '../productInfo/ProductInfoSwitcher'
  )
);

const { categoryId } = window.inlinePlp;
const {
  abTastyBestSellerThreshold,
  advancedCommerce: { isListingPage },
  locale,
} = window.inlineGlobalConfig;

class ProductItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentSlide: 0,
      isArrowClicked: false,
      lazyLoaded: false,
      screenToRender: null,
      sizeSelectorHeight: null,
      isXLargeUp: mediaQueries.is_xlarge_up(),
    };

    this.productRef = null;

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleProductClick = this.handleProductClick.bind(this);

    this.resizeHandler = this.resizeHandler.bind(this);
    this.handleArrowLeft = this.handleArrowLeft.bind(this);
    this.handleArrowRight = this.handleArrowRight.bind(this);

    this.updateScreen = this.updateScreen.bind(this);
    this.updateSizeSelector = this.updateSizeSelector.bind(this);
    this.onAddToBagCallback = this.onAddToBagCallback.bind(this);
  }

  componentDidMount() {
    const {
      lastProductLoaded,
      referrerProductId,
      cleanReferrerProductId,
      resultPosition,
      totalFetchedProducts,
      addProductsReferences,
    } = this.props;

    addProductsReferences(this.productRef);

    if (totalFetchedProducts === resultPosition + 1) {
      lastProductLoaded();
    }

    // TODO Fix intermittent bug: After focusing on referrer product, the page scrolls to page bottom
    if (referrerProductId && referrerProductId === this.productRef.dataset.sku) {
      const isLargeUp = mediaQueries.is_large_up();
      this.productRef.scrollIntoView(true);

      if (isIOS() && !isLargeUp) {
        const header = document.querySelector('.header');
        const headerPromo = document.querySelector('.header__promo');
        const headerHeight = header ? header.offsetHeight : 0;
        const headerPromoHeight = headerPromo ? headerPromo.offsetHeight : 0;
        const topSpace = headerHeight + headerPromoHeight;
        window.scrollTo(0, window.pageYOffset - topSpace);
      }

      cleanReferrerProductId();
    }

    this.resizeHandler();
    window.addEventListener('resize', this.resizeHandler);
  }

  componentDidUpdate(prevProps, prevState) {
    // Main image should be lazyLoaded first from handleLazyLoadObserver()
    // There's no way to check it except for checking the className,
    // Because changing the state from within intersectionObserver is a
    // very heavy operation, thus, performance-wise, it's better to do it this way here
    if (!prevState.lazyLoaded && this.productRef.classList.contains('lazy-loaded')) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ lazyLoaded: true });
    }

    return null;
  }

  componentWillUnmount() {
    const { removeProductsReferences } = this.props;
    window.removeEventListener('resize', this.resizeHandler);
    removeProductsReferences(this.productRef);
  }

  onAddToBagCallback() {
    this.updateScreen('addToBagConfirmation');
  }

  handleMouseEnter() {
    const isXLargeUp = mediaQueries.is_xlarge_up();
    if (isXLargeUp) {
      this.setState(prevState => ({
        currentSlide: prevState.isArrowClicked ? prevState.currentSlide : 1,
      }));
    }
  }

  handleMouseLeave() {
    const isXLargeUp = mediaQueries.is_xlarge_up();
    if (isXLargeUp) {
      this.setState(prevState => ({
        currentSlide: prevState.isArrowClicked ? prevState.currentSlide : 0,
      }));
    }
  }

  handleArrowLeft() {
    const { product } = this.props;
    this.setState(prevState => ({
      currentSlide: prevState.currentSlide === 0 ? product.imageSet.length - 1 : prevState.currentSlide - 1,
      isArrowClicked: true,
    }));
  }

  handleArrowRight() {
    const { product } = this.props;
    this.setState(prevState => ({
      currentSlide: prevState.currentSlide === product.imageSet.length - 1 ? 0 : prevState.currentSlide + 1,
      isArrowClicked: true,
    }));
  }

  handleKeyDown(e) {
    if (isRightArrowPressed(e)) {
      this.handleArrowRight();
      return;
    }

    if (isLeftArrowPressed(e)) {
      this.handleArrowRight();
    }
  }

  updateSizeSelector(sizeSelectorHeight) {
    this.setState({ sizeSelectorHeight });
  }

  updateScreen(screenToRender) {
    const isMediumUp = mediaQueries.is_medium_up();

    this.setState({
      screenToRender: !isMediumUp || (isMediumUp && screenToRender !== 'zoom') ? screenToRender : null,
    });
  }

  resizeHandler() {
    const { screenToRender, isXLargeUp } = this.state;
    this.updateScreen(screenToRender);

    const isXLargeUpNow = mediaQueries.is_xlarge_up();
    if (isXLargeUpNow !== isXLargeUp) {
      this.setState({
        isXLargeUp: isXLargeUpNow,
      });
    }
  }

  handleProductClick() {
    const { product, resultPosition, isGridView } = this.props;
    saveProductReferrer(categoryId, product, isGridView);
    dlTrackProductClick({
      product,
      position: resultPosition,
    });
    dlSelectPLPOrSRPItem(product, resultPosition);
  }

  render() {
    const { currentSlide, screenToRender, sizeSelectorHeight, isXLargeUp } = this.state;
    const {
      isGridView,
      LazyImageComponent,
      onSizeDeselected,
      onSizeSelected,
      product,
      uniqueTemplateKey,
      updateProductDetails,
      onAddToWishlist,
      openNotificationModal,
      isNotificationModalOpen,
      intl,
    } = this.props;

    const heroBadges = product.heroBadges !== null ? product.heroBadges : [];
    const promoBadges = product.promoBadges !== null ? product.promoBadges : [];

    const heroBadgeElements = heroBadges.length > 0 && heroBadges.map(badge => <HeroBadge key={badge} value={badge} />);
    const promoBadgeElements =
      promoBadges.length > 0 && promoBadges.map(badge => <PromoBadge key={badge} value={badge} />);

    const imgAltText =
      product?.altTextSet?.[currentSlide]?.[`${locale.language}-${locale.country}`] ||
      intl.formatMessage({
        id: 'products.plp.image.altText',
        description: 'Product image',
      });

    const sliderImageSection = (
      <div className={`product-list__slider product-list__slider--${product.id}`}>
        <div className="product-list__slider-image" aria-live="polite">
          <LazyImageComponent
            alt={isListingPage ? imgAltText : product.name}
            className="product-image"
            mediaUrl={product.imageSet[currentSlide]}
            sizeS={isGridView ? '95vw' : '45vw'}
            sizeM={isGridView ? '45vw' : '30vw'}
            sizeL={isGridView ? '30vw' : '22vw'}
            uniqueId={product.id}
          />
        </div>
      </div>
    );

    return (
      <Fragment>
        <div
          className="grid__list-item"
          data-sku={product.id}
          data-best-selling={`${
            product.bestSellerRanking != null && product.bestSellerRanking <= abTastyBestSellerThreshold
          }`}
          ref={ref => {
            this.productRef = ref;
          }}
        >
          {promoBadgeElements && <div className="product-badge__promo-wrapper">{promoBadgeElements}</div>}
          <div
            className={`product-list__image-wrapper product-list__image-wrapper--with-lazy-img ${
              product.inWishlist ? 'product-list__image-wrapper--in-wishlist' : ''
            }`}
          >
            {isXLargeUp ? (
              <a
                className="product-link"
                href={product.url}
                onClick={e => this.handleProductClick(e)}
                onMouseEnter={e => this.handleMouseEnter(e)}
                onMouseLeave={e => this.handleMouseLeave(e)}
                onKeyDown={this.handleKeyDown}
              >
                {sliderImageSection}
              </a>
            ) : (
              <a className="product-link" href={product.url} onClick={e => this.handleProductClick(e)}>
                {sliderImageSection}
              </a>
            )}
            <ArrowsAndBottomIcons
              onArrowLeftClick={() => this.handleArrowLeft()}
              onArrowRightClick={() => this.handleArrowRight()}
              onZoomClick={() => this.updateScreen('zoom')}
              onWishlistClick={() => this.updateScreen('wishlist')}
              onBagClick={() => this.updateScreen('addToBag')}
            />
          </div>
          <a className="product-link" href={product.url} onClick={e => this.handleProductClick(e)}>
            <ProductData product={product} />
            {heroBadgeElements && <div className="product-badge__hero-wrapper">{heroBadgeElements}</div>}
            <StarRating rating={product.averageRating} reviews={product.numberOfReviews} />
          </a>
          {screenToRender && (
            <TrapFocus enabled={!!screenToRender && !isNotificationModalOpen}>
              <React.Suspense fallback={<LoadingIndicator fallbackUi />}>
                <ProductInfoSwitcher
                  onAddToBagCallback={this.onAddToBagCallback}
                  onAddToWishlist={onAddToWishlist}
                  onSizeDeselected={onSizeDeselected}
                  onSizeSelected={onSizeSelected}
                  product={product}
                  screenToRender={screenToRender}
                  sizeSelectorHeight={sizeSelectorHeight}
                  sliderImages={product.imageSet}
                  uniqueTemplateKey={uniqueTemplateKey}
                  updateProductDetails={updateProductDetails}
                  updateScreen={this.updateScreen}
                  updateSizeSelector={this.updateSizeSelector}
                  openNotificationModal={openNotificationModal}
                />
              </React.Suspense>
            </TrapFocus>
          )}
        </div>
      </Fragment>
    );
  }
}

ProductItem.defaultProps = {
  referrerProductId: null,
};

ProductItem.propTypes = {
  LazyImageComponent: PropTypes.func.isRequired,
  cleanReferrerProductId: PropTypes.func.isRequired,
  isGridView: PropTypes.bool.isRequired,
  lastProductLoaded: PropTypes.func.isRequired,
  onSizeDeselected: PropTypes.func.isRequired,
  onSizeSelected: PropTypes.func.isRequired,
  product: PropTypes.shape({
    imageSet: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  referrerProductId: PropTypes.string,
  resultPosition: PropTypes.number.isRequired,
  totalFetchedProducts: PropTypes.number.isRequired,
  uniqueTemplateKey: PropTypes.string.isRequired,
  updateProductDetails: PropTypes.func.isRequired,
  addProductsReferences: PropTypes.func.isRequired,
  removeProductsReferences: PropTypes.func.isRequired,
  onAddToWishlist: PropTypes.func.isRequired,
  isNotificationModalOpen: PropTypes.bool.isRequired,
  openNotificationModal: PropTypes.func.isRequired,
};

export default injectIntl(ProductItem);
